import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionApi, SearchLogItem, HistoryResponse } from '../api/subscriptionApi';
import { useEffect } from 'react';

const PAGE_SIZE_OPTIONS = [10, 50, 100];

const exportCSV = (data: SearchLogItem[]) => {
    const header = ['#', 'Search Term', 'Date & Time', 'IP Address'];
    const rows = data.map((r, i) => [i + 1, r.searchTerm, new Date(r.searchedAt).toLocaleString(), r.ipAddress]);
    const csv = [header, ...rows].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `history-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
};

const SkeletonRow = () => (
    <tr>{[10, 55, 30, 25, 14].map((w, i) => (
        <td key={i} className="px-4 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${w}%` }}/></td>
    ))}</tr>
);

const HistoryPage = () => {
    const navigate = useNavigate();
    const { isLoggedIn } = useAuth();
    const [history, setHistory] = useState<HistoryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [filter, setFilter] = useState('');

    const load = useCallback(async (p: number, ps: number) => {
        if (!isLoggedIn) return;
        setLoading(true);
        try { const d = await subscriptionApi.getSearchHistory(p, ps); setHistory(d); }
        catch { } finally { setLoading(false); }
    }, [isLoggedIn]);

    useEffect(() => { if (isLoggedIn) load(1, pageSize); }, [isLoggedIn, load, pageSize]);

    const filtered = (history?.logs || []).filter(r => !filter || r.searchTerm.toLowerCase().includes(filter.toLowerCase()));
    const totalPages = history?.totalPages || 1;

    const getPages = (): (number | '...')[] => {
        if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
        const p: (number | '...')[] = [1];
        if (page > 3) p.push('...');
        for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) p.push(i);
        if (page < totalPages - 2) p.push('...');
        p.push(totalPages);
        return p;
    };

    if (!isLoggedIn) return (
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
            <div className="w-16 h-16 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="text-teal-500" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Search History</h2>
            <p className="text-sm text-gray-400 mb-6">Sign in to view your search history</p>
            <button onClick={() => navigate('/login')} className="px-6 py-2.5 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 transition-all">Sign In</button>
        </div>
    );

    return (
        <div className="max-w-5xl mx-auto px-4 py-6">
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-900">Search History</h1>
                <p className="text-sm text-gray-400 mt-0.5">All your previous medical code searches</p>
            </div>

            <div className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between flex-wrap gap-3">
                    <p className="text-[11px] text-gray-400">
                        {history?.total ? <><span className="font-bold text-gray-600 font-mono">{history.total}</span> total searches</> : 'Loading...'}
                    </p>
                    <button onClick={async () => { try { exportCSV(await subscriptionApi.getAllHistory()); } catch {} }} disabled={!history?.total}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg text-gray-600 hover:bg-green-50 hover:text-green-700 transition-all disabled:opacity-40">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                        Export CSV
                    </button>
                </div>

                <div className="px-5 py-2.5 bg-gray-50/50 border-b border-gray-50 flex items-center justify-between gap-3 flex-wrap">
                    <div className="relative">
                        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                        <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter..." className="pl-7 pr-3 py-1.5 text-[11px] border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 text-gray-700 bg-white w-36"/>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-gray-400 font-semibold">Show:</span>
                        {PAGE_SIZE_OPTIONS.map(ps => (
                            <button key={ps} onClick={() => { setPageSize(ps); setPage(1); load(1, ps); }}
                                className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all ${pageSize === ps ? 'bg-teal-600 text-white' : 'bg-white text-gray-500 hover:bg-teal-50 border border-gray-200'}`}>{ps}</button>
                        ))}
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead><tr className="border-b border-gray-100">
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase w-10">#</th>
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase">Search Term</th>
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-[10px] font-bold text-gray-400 uppercase hidden md:table-cell">IP</th>
                            <th className="px-4 py-3 w-16"></th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-50">
                            {loading ? Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i}/>) :
                            filtered.length === 0 ? <tr><td colSpan={5} className="px-4 py-12 text-center text-sm text-gray-400">{filter ? 'No results' : 'No searches yet'}</td></tr> :
                            filtered.map((row, idx) => {
                                const date = new Date(row.searchedAt);
                                return (
                                    <tr key={row.id} className="hover:bg-teal-50/30 group">
                                        <td className="px-4 py-3"><span className="text-[11px] font-mono text-gray-300">{(page - 1) * pageSize + idx + 1}</span></td>
                                        <td className="px-4 py-3"><span className="text-[13px] font-semibold text-gray-800 group-hover:text-teal-700">{row.searchTerm}</span></td>
                                        <td className="px-4 py-3">
                                            <p className="text-[12px] text-gray-600 font-medium">{date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                                            <p className="text-[10px] text-gray-400 font-mono">{date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
                                        </td>
                                        <td className="px-4 py-3 hidden md:table-cell"><span className="text-[10px] font-mono text-gray-400 bg-gray-50 px-2 py-0.5 rounded">{row.ipAddress}</span></td>
                                        <td className="px-4 py-3"><button onClick={() => navigate(`/search?q=${encodeURIComponent(row.searchTerm)}`)} className="text-[11px] font-semibold text-teal-600 hover:underline">→</button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {history && history.total > 0 && (
                    <div className="px-5 py-3.5 border-t border-gray-50 flex items-center justify-between flex-wrap gap-3">
                        <p className="text-[11px] text-gray-400">
                            <span className="font-bold text-gray-600">{(page - 1) * pageSize + 1}</span> – <span className="font-bold text-gray-600">{Math.min(page * pageSize, history.total)}</span> of <span className="font-bold text-gray-600">{history.total}</span>
                        </p>
                        <div className="flex items-center gap-1">
                            <button onClick={() => { setPage(page - 1); load(page - 1, pageSize); }} disabled={page === 1} className="px-2.5 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-30">‹</button>
                            {getPages().map((p, i) => p === '...' ? <span key={`e${i}`} className="px-1 text-gray-300 text-[11px]">…</span> :
                                <button key={p} onClick={() => { setPage(p as number); load(p as number, pageSize); }}
                                    className={`w-7 h-7 text-[11px] font-bold rounded-lg ${page === p ? 'bg-teal-600 text-white' : 'border border-gray-200 text-gray-600 hover:bg-teal-50'}`}>{p}</button>
                            )}
                            <button onClick={() => { setPage(page + 1); load(page + 1, pageSize); }} disabled={page === totalPages} className="px-2.5 py-1.5 text-[11px] font-semibold border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-30">›</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HistoryPage;